import { createRouter, createWebHistory } from 'vue-router'
import { CONTACT_ROUTES } from '@/modules/contacts/routes'
import { APPOINTMENT_ROUTES } from '@/modules/appointments/routes'
import { QUOTE_ROUTES } from '@/modules/quotes/routes'
import { SERVICE_ROUTES } from '@/modules/services/routes'
import { PRODUCT_ROUTES } from '@/modules/products/routes'
import { PRICING_ROUTES } from '@/modules/pricing/routes'
import { SETTINGS_ROUTES } from '@/modules/settings/routes'
import { LOCATION_ROUTES } from '@/modules/locations/routes'
import { PIPELINE_ROUTES } from '@/modules/pipelines/routes'
import { JOB_ROUTES, FIELD_JOB_ROUTES } from '@/modules/jobs/routes'
import { FORM_ROUTES } from '@/modules//forms/forms/routes'
import {
  FORM_SUBMISSION_ROUTES,
  FIELD_FORM_SUBMISSION_ROUTES,
} from '@/modules/forms/formSubmissions/routes'
import { SCHEDULE_ROUTES, FIELD_SCHEDULE_ROUTES } from '@/modules/scheduleEntries/routes'
import { FINANCIAL_ROUTES } from '@/modules/financials/routes'
import { AUTH_ROUTES } from '@/modules/auth/auth.routes'
import { USER_ROUTES } from '@/modules/auth/user.routes'
import { ANALYTIC_ROUTES } from '@/modules/analytics/routes'
import { API_KEY_ROUTES } from '@/modules/apiKeys/routes'
import {
  PAYROLL_ROUTES,
  FIELD_PAYROLL_ROUTES,
  COMMISSION_PLAN_ROUTES,
} from '@/modules/payroll/routes'
import AppLayout from '@/layouts/AppLayout.vue'
import SettingsLayout from '@/layouts/SettingsLayout.vue'
import FieldLayout from '@/layouts/FieldLayout.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: AppLayout,
      children: [
        ...CONTACT_ROUTES,
        ...APPOINTMENT_ROUTES,
        ...QUOTE_ROUTES,
        ...JOB_ROUTES,
        ...FORM_SUBMISSION_ROUTES,
        ...SCHEDULE_ROUTES,
        ...FINANCIAL_ROUTES,
        ...ANALYTIC_ROUTES,
        ...PAYROLL_ROUTES,
        {
          path: '/organization/setup',
          name: 'SetupOrganization',
          component: () => import('@/modules/auth/organizations/views/SetupOrganization.vue'),
        },
      ],
      meta: { requiresAuth: true, allowedRoles: ['org:owner', 'org:admin', 'org:member'] },
    },
    {
      path: '/settings',
      component: SettingsLayout,
      children: [
        ...SERVICE_ROUTES,
        ...PRODUCT_ROUTES,
        ...PRICING_ROUTES,
        ...LOCATION_ROUTES,
        ...SETTINGS_ROUTES,
        ...PIPELINE_ROUTES,
        ...FORM_ROUTES,
        ...USER_ROUTES,
        ...COMMISSION_PLAN_ROUTES,
        ...API_KEY_ROUTES,
      ],
      meta: { requiresAuth: true, allowedRoles: ['org:owner', 'org:admin'] },
    },
    {
      path: '/developers/docs',
      name: 'ApiDocs',
      component: () => import('@/modules/apiKeys/views/ApiDocs.vue'),
      // Protected so only Owners and Admins can view the docs
      meta: { requiresAuth: true, allowedRoles: ['org:owner', 'org:admin'] },
    },
    {
      path: '/field',
      component: FieldLayout,
      children: [
        ...FIELD_SCHEDULE_ROUTES,
        ...FIELD_PAYROLL_ROUTES,
        ...FIELD_JOB_ROUTES,
        ...FIELD_FORM_SUBMISSION_ROUTES,
      ],
      meta: { requiresAuth: true, allowedRoles: ['org:painter', 'org:subcontractor'] },
    },
    {
      path: '/auth',
      children: [...AUTH_ROUTES],
      meta: { requiresAuth: false },
    },
    {
      path: '/portal',
      children: [
        {
          path: 'quotes/:quoteId',
          name: 'PortalQuote',
          component: () => import('@/modules/quotes/views/PortalQuote.vue'),
        },
      ],
      meta: { requiresAuth: false },
    },
  ],
})

router.beforeEach(async (to, from) => {
  if (!to.meta.requiresAuth) {
    return true
  }

  const checkClerkLoaded = () => {
    return new Promise((resolve) => {
      if (window.Clerk && window.Clerk.loaded) {
        resolve()
      } else {
        const interval = setInterval(() => {
          if (window.Clerk && window.Clerk.loaded) {
            clearInterval(interval)
            resolve()
          }
        }, 50)
      }
    })
  }

  await checkClerkLoaded()

  if (!window.Clerk.session) {
    // NEW: Intercept Clerk invitation tickets!
    if (to.query.__clerk_ticket) {
      return {
        name: 'SignUp',
        query: { ...to.query },
      }
    }

    return { name: 'SignIn', query: { redirect: to.fullPath } }
  }

  const user = window.Clerk.user
  const memberships = user.organizationMemberships || []
  let activeOrg = window.Clerk.organization

  if (memberships.length === 0 && to.name !== 'CreateOrganization') {
    return { name: 'CreateOrganization' }
  }

  if (memberships.length > 0 && !activeOrg) {
    try {
      await window.Clerk.setActive({ organization: memberships[0].organization.id })
      activeOrg = window.Clerk.organization
    } catch (error) {
      console.error('Failed to auto-activate organization:', error)
    }
  }

  if (activeOrg) {
    const isSetupComplete = activeOrg.publicMetadata?.setup_complete === true

    if (!isSetupComplete && to.name !== 'SetupOrganization') {
      return { name: 'SetupOrganization' }
    }

    if (isSetupComplete && to.name === 'SetupOrganization') {
      return { path: '/' }
    }

    // 1. EXTRACT THE ROLE HERE FIRST!
    const activeMembership = memberships.find((m) => m.organization.id === activeOrg.id)
    const role = activeMembership?.role || 'guest'

    // 2. NOW WE CAN SAFELY CHECK IT AGAINST ALLOWED ROLES
    if (to.meta.allowedRoles && !to.meta.allowedRoles.includes(role)) {
      if (role === 'org:painter' || role === 'org:subcontractor') {
        return { path: '/field/schedule' } // Perfect redirect!
      }
      return { path: '/' }
    }

    // 3. ROOT REDIRECTS
    if (to.path === '/') {
      const analyticPerms = activeMembership?.publicMetadata?.analytic_permissions || []

      if (
        role === 'org:owner' ||
        (role === 'org:admin' &&
          (analyticPerms.includes('sales:location') || analyticPerms.includes('sales:own'))) ||
        (role === 'org:member' &&
          (analyticPerms.includes('sales:location') || analyticPerms.includes('sales:own')))
      ) {
        return { name: 'SalesAnalytics' }
      }

      if (
        role === 'org:owner' ||
        (role === 'org:admin' &&
          (analyticPerms.includes('production:location') ||
            analyticPerms.includes('production:own'))) ||
        (role === 'org:member' &&
          (analyticPerms.includes('production:location') ||
            analyticPerms.includes('production:own')))
      ) {
        return { name: 'ProductionAnalytics' }
      }

      if (role === 'org:painter' || role === 'org:subcontractor') {
        return { path: '/field/schedule' }
      }

      return { name: 'NewLeads' }
    }
  }

  if (to.path.startsWith('/settings')) {
    const hasAdminRole = window.Clerk.session.checkAuthorization({ role: 'org:admin' })
    const hasOwnerRole = window.Clerk.session.checkAuthorization({ role: 'org:owner' })

    if (!hasAdminRole && !hasOwnerRole) {
      return { path: '/' }
    }
  }

  if (to.path.startsWith('/sales-analytics')) {
    const activeMembership = memberships.find((m) => m.organization.id === activeOrg.id)
    const role = activeMembership?.role
    const rawPerms = activeMembership?.publicMetadata?.analytic_permissions
    const analyticPerms = Array.isArray(rawPerms) ? rawPerms : []

    const hasAnalyticsAccess =
      role === 'org:owner' ||
      (role === 'org:admin' &&
        (analyticPerms.includes('sales:location') || analyticPerms.includes('sales:own'))) ||
      (role === 'org:member' &&
        (analyticPerms.includes('sales:location') || analyticPerms.includes('sales:own')))

    if (!hasAnalyticsAccess) {
      return { path: '/' }
    }
  }

  // admin payroll lock down
  if (
    to.path.startsWith('/payroll') ||
    to.path.startsWith('/admin-time-off') ||
    to.path.startsWith('/admin-commissions') ||
    to.path.startsWith('/admin-time-tracking')
  ) {
    const activeMembership = memberships.find((m) => m.organization.id === activeOrg.id)
    const role = activeMembership?.role

    if (role !== 'org:owner' && role !== 'org:admin') {
      return { path: '/' }
    }
  }

  return true
})
export default router
